import { useWeb3DebugContext } from "@/components/Web3/Debug/State";
import type { Networkish, SupWeb3Address } from "@/types";
import { useMemo } from "react";

import useApi from "@/hooks/api/useApi";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";

export interface RouterSimulationResult {
  id: string;
  network_id: Networkish;
  error_message: string;
  success: boolean;
}
export interface ResponseRouterSimulation {
  status: number;
  message: string;
  data: RouterSimulationResult;
}

interface useRouterSimulationParams {
  chainId?: Networkish;
  txData?: SupWeb3Address;
  value?: bigint;
  isSuperPool?: boolean;
  enabled?: boolean;
  onSuccess?: (data: ResponseRouterSimulation) => void;
  onFail?: (error: Error) => void;
}

/**
 * @package api:utils
 * @link https://api-utils.superform.xyz/docs#/Superform/get_superform_router_simulation_routersimulation_get
 */
export default function useRouterSimulation({ chainId, txData, value = 0n, isSuperPool = false, enabled = true, onFail, onSuccess }: useRouterSimulationParams) {
  const userAddress = useWeb3UserAddress();
  const { addRouterSimulation } = useWeb3DebugContext();

  const params = useMemo(() => {
    if (!txData || !chainId || !enabled || !userAddress) {
      addRouterSimulation?.(undefined);
      return undefined;
    }
    return {
      user_address: userAddress,
      chain_id: chainId,
      tx_data: txData,
      value: value.toString(),
      is_router: !isSuperPool,
    };
  }, [txData, chainId, enabled, userAddress, value, isSuperPool, addRouterSimulation]);

  const { data, isLoading, isError, mutate, error } = useApi<RouterSimulationResult, ResponseRouterSimulation>(
    {
      path: `/simulation/router/`,
      method: "POST",
      data: params,
    },
    {
      strategy: "fully-cached",
      enabled: !!params,
      select: data => {
        if (data.status !== 200) {
          const error = new Error(data.message);
          onFail?.(error);
          addRouterSimulation?.(undefined);
          throw new Error(data.message);
        }

        onSuccess?.(data);

        const preparedData = data.data
          ? data.data
          : {
              id: "",
              network_id: chainId || "",
              error_message: "",
              success: true,
            };

        addRouterSimulation?.(preparedData);

        return preparedData;
      },
    }
  );

  return {
    data,
    error,
    isLoading,
    isError,
    mutate,
  };
}
